import React from 'react';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand';

interface StatusBadgeProps {
  /** Label text */
  label: string;
  /** Visual variant */
  variant?: BadgeVariant;
  /** Show a pulse dot */
  pulse?: boolean;
  /** Size */
  size?: 'sm' | 'md';
  /** Additional className */
  className?: string;
}

const variantStyles: Record<BadgeVariant, { bg: string; text: string; dot: string }> = {
  success: {
    bg: 'bg-accent-success/15',
    text: 'text-accent-success',
    dot: 'bg-accent-success',
  },
  warning: {
    bg: 'bg-accent-warning/15',
    text: 'text-accent-warning',
    dot: 'bg-accent-warning',
  },
  danger: {
    bg: 'bg-accent-danger/15',
    text: 'text-accent-danger',
    dot: 'bg-accent-danger',
  },
  info: {
    bg: 'bg-accent-info/15',
    text: 'text-accent-info',
    dot: 'bg-accent-info',
  },
  neutral: {
    bg: 'bg-txt-muted/15',
    text: 'text-txt-muted',
    dot: 'bg-txt-muted',
  },
  brand: {
    bg: 'bg-accent-brand/15',
    text: 'text-accent-brand',
    dot: 'bg-accent-brand',
  },
};

/**
 * StatusBadge — Pill-shaped badge with optional pulse dot.
 */
export default function StatusBadge({
  label,
  variant = 'neutral',
  pulse = false,
  size = 'sm',
  className = '',
}: StatusBadgeProps) {
  const styles = variantStyles[variant];
  const sizeClasses = size === 'sm' ? 'text-xs px-2.5 py-0.5' : 'text-sm px-3 py-1';

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-sans font-medium
        ${styles.bg} ${styles.text} ${sizeClasses} ${className}
      `}
    >
      {pulse && (
        <span className="relative flex h-2 w-2">
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${styles.dot}`}
          />
          <span
            className={`relative inline-flex rounded-full h-2 w-2 ${styles.dot}`}
          />
        </span>
      )}
      {label}
    </span>
  );
}
